import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Login from './components/Login';
import DashboardLayout from './components/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedSectionRoute from './components/ProtectedSectionRoute';
import ProtectedPortalRoute from './components/ProtectedPortalRoute';
import PosteScan from './components/PosteScan';
import GestionProfils from './components/GestionProfils';
import GestionOperateurs from './components/GestionOperateurs';
import GestionProduits from './components/GestionProduits';
import GestionCodesBarres from './components/GestionCodesBarres';
import PlanningProductionV2 from './components/PlanningProductionV2';
import GestionRevendeurs from './components/GestionRevendeurs';
import GestionStock from './components/GestionStock';
import FFMModule from './components/FFMModule';
import FFMValidationBAT from './components/FFMValidationBAT';
import FFMLogin from './components/FFMLogin';
import FFMPortalLayout from './components/FFMPortalLayout';
import {
  PortalCommandes,
  PortalPilotes,
  PortalProduits,
  PortalOperateurs,
  PortalProfils,
  PortalAide,
} from './components/FFMPortalPages';
import NotificationSystem from './components/NotificationSystem';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Session portail FFM (indépendante du backoffice)
  const [ffmSession, setFfmSession] = useState(() => {
    try {
      const raw = localStorage.getItem('ffm_session');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    // Vérifier la connexion à Supabase
    const testConnection = async () => {
      try {
        const { error } = await supabase.from('profils').select('count');
        if (error) {
          console.error('Erreur connexion Supabase:', error);
        } else {
          console.log('✔ Connexion Supabase réussie !');
        }
      } catch (err) {
        console.error('Erreur:', err);
      }
      setLoading(false);
    };
    
    testConnection();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    // Sauvegarder l'opérateur dans localStorage pour NotificationSystem
    localStorage.setItem('operateur', JSON.stringify(userData));
    console.log('✅ Opérateur sauvegardé dans localStorage:', userData);
  };

  const handleLogout = () => {
    setUser(null);
    // Nettoyer le localStorage
    localStorage.removeItem('operateur');
    console.log('🚪 Opérateur retiré du localStorage');
  };

  if (loading) {
    return <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      backgroundColor: 'black',
      color: 'cyan'
    }}>
      Chargement...
    </div>;
  }

  return (
    <BrowserRouter>
      {/* Système de notifications global - uniquement si connecté */}
      {user && <NotificationSystem />}
      
      <Routes>
        {/* Routes pour les postes de scan (Raspberry Pi) */}
        <Route path="/poste" element={<PosteScan />} />
        <Route path="/poste-scan" element={<PosteScan />} />
        
        {/* Route de login */}
        <Route 
          path="/admin_4Sd6c3P7/login" 
          element={
            user ? (
              // Toujours rediriger vers planning après login
              <Navigate to="/admin_4Sd6c3P7/planning" replace />
            ) : (
              <Login onLogin={handleLogin} />
            )
          } 
        />
        
        {/* Routes protégées */}
        <Route 
          path="/admin_4Sd6c3P7/profils" 
          element={
            <ProtectedRoute user={user}>
              <ProtectedSectionRoute user={user} requiredSection="profils">
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <GestionProfils />
                </DashboardLayout>
              </ProtectedSectionRoute>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/admin_4Sd6c3P7/operateurs" 
          element={
            <ProtectedRoute user={user}>
              <ProtectedSectionRoute user={user} requiredSection="operateurs">
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <GestionOperateurs />
                </DashboardLayout>
              </ProtectedSectionRoute>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/admin_4Sd6c3P7/produits" 
          element={
            <ProtectedRoute user={user}>
              <ProtectedSectionRoute user={user} requiredSection="produits">
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <GestionProduits />
                </DashboardLayout>
              </ProtectedSectionRoute>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/admin_4Sd6c3P7/codebarre" 
          element={
            <ProtectedRoute user={user}>
              <ProtectedSectionRoute user={user} requiredSection="codebarre">
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <GestionCodesBarres />
                </DashboardLayout>
              </ProtectedSectionRoute>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/admin_4Sd6c3P7/planning" 
          element={
            <ProtectedRoute user={user}>
              <ProtectedSectionRoute user={user} requiredSection="planning">
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <PlanningProductionV2 />
                </DashboardLayout>
              </ProtectedSectionRoute>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/admin_4Sd6c3P7/revendeurs" 
          element={
            <ProtectedRoute user={user}>
              <ProtectedSectionRoute user={user} requiredSection="revendeurs">
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <GestionRevendeurs />
                </DashboardLayout>
              </ProtectedSectionRoute>
            </ProtectedRoute>
          } 
        />

        <Route 
          path="/admin_4Sd6c3P7/ffm" 
          element={
            <ProtectedRoute user={user}>
              <ProtectedSectionRoute user={user} requiredSection="ffm">
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <FFMModule />
                </DashboardLayout>
              </ProtectedSectionRoute>
            </ProtectedRoute>
          } 
        />
        
        
        
        <Route 
          path="/admin_4Sd6c3P7/stock" 
          element={
            <ProtectedRoute user={user}>
              <ProtectedSectionRoute user={user} requiredSection="stock">
                <DashboardLayout user={user} onLogout={handleLogout}>
                  <GestionStock />
                </DashboardLayout>
              </ProtectedSectionRoute>
            </ProtectedRoute>
          } 
        />

        {/* Redirection pour /admin_4Sd6c3P7/ vers login */}
        <Route 
          path="/admin_4Sd6c3P7" 
          element={<Navigate to="/admin_4Sd6c3P7/login" replace />} 
        />
        
        {/* Route catch-all pour /admin_4Sd6c3P7/* invalides - rediriger vers login */}
        <Route 
          path="/admin_4Sd6c3P7/*" 
          element={<Navigate to="/admin_4Sd6c3P7/login" replace />} 
        />
        
        {/* Page publique validation BAT */}
        <Route path="/ffm/bat/:token" element={<FFMValidationBAT />} />

        {/* ── Portail FFM (ffm.factorylab3d.com) ───────────────────────── */}
        <Route
          path="/ffm-customlab/login"
          element={
            ffmSession
              ? <Navigate to="/ffm-customlab/dashboard/commandes" replace />
              : <FFMLogin onLogin={setFfmSession} />
          }
        />
        <Route
          path="/ffm-customlab/dashboard"
          element={
            <ProtectedPortalRoute session={ffmSession}>
              <FFMPortalLayout session={ffmSession} onLogout={() => setFfmSession(null)} />
            </ProtectedPortalRoute>
          }
        >
          <Route index element={<Navigate to="commandes" replace />} />
          <Route path="commandes"  element={<ProtectedPortalRoute session={ffmSession} requiredRole="commandes"><PortalCommandes /></ProtectedPortalRoute>} />
          <Route path="pilotes"    element={<ProtectedPortalRoute session={ffmSession} requiredRole="pilotes"><PortalPilotes /></ProtectedPortalRoute>} />
          <Route path="produits"   element={<ProtectedPortalRoute session={ffmSession} requiredRole="produits"><PortalProduits /></ProtectedPortalRoute>} />
          <Route path="operateurs" element={<ProtectedPortalRoute session={ffmSession} requiredRole="operateurs"><PortalOperateurs /></ProtectedPortalRoute>} />
          <Route path="profils"    element={<ProtectedPortalRoute session={ffmSession} requiredRole="profils"><PortalProfils /></ProtectedPortalRoute>} />
          <Route path="aide"       element={<PortalAide />} />
        </Route>
        <Route path="/ffm-customlab" element={<Navigate to="/ffm-customlab/login" replace />} />
        <Route path="/ffm-customlab/*" element={<Navigate to="/ffm-customlab/login" replace />} />

        {/* Page vide pour la racine et toute autre URL */}
        <Route 
          path="*" 
          element={
            <div style={{
              backgroundColor: 'black',
              height: '100vh',
              width: '100vw',
              margin: 0,
              padding: 0
            }} />
          } 
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;